import { ClientInsuranceStatus, MatchAssignmentType } from '@sondermind/utilities/models-matching';
import { ITimeProposalApiResponse, SMCalendarEvent, TimeProposal } from '@sondermind/utilities/models-scheduling';

import { ClientReferral, IClientReferralApiResponse } from './client-referral.model';
import { InternalReferralDetails, InternalReferralDetailsResponse } from './internal-referral-details.model';

export class ClientReferralMember {
  id: number;
  platformIdentity: string;
  nonClinicalChatRoomUuid: string;
  userId: number;
  aasmState: string;
  notifiedAt: string;
  declinedAt: string;
  acceptedAt: string;
  patientId: number;
  clientReferralId: number;
  clientReferral: ClientReferral | null;
  smCalendarEvent: SMCalendarEvent[] = [];
  blockNumberMaxed = false;
  sessionRequested: boolean = false;
  requestedSessionType: RequestedSessionType;
  urgent: boolean;
  matchAssignment: MatchAssignmentType;
  timeProposals: TimeProposal[];
  timeProposal: boolean;
  clientGoals: string;
  clientAbout: string;
  clientInsuranceStatus: string;
  internalReferralDetail: InternalReferralDetails;

  constructor(json: IClientReferralMemberApiResponse) {
    this.id = json.id;
    this.nonClinicalChatRoomUuid = json.non_clinical_chat_room_uuid;
    this.userId = json.user_id;
    this.aasmState = json.aasm_state;
    this.notifiedAt = json.notified_at;
    this.declinedAt = json.declined_at;
    this.acceptedAt = json.accepted_at;
    this.patientId = json.patient_id;
    this.clientReferralId = json.client_referral_id;
    this.clientReferral = json.client_referral ? new ClientReferral(json.client_referral) : null;
    this.smCalendarEvent = json.calendar_events;
    this.blockNumberMaxed = json.block_number_maxed;
    this.sessionRequested = json.session_requested;
    this.requestedSessionType = json.requested_session_type;
    this.matchAssignment = json.match_assignment;
    this.timeProposals = json.time_proposals ? json.time_proposals.map((tp) => new TimeProposal(tp)) : [];
    this.timeProposal = json.time_proposal ?? false;
    this.clientGoals = json.client_goals;
    this.clientAbout = json.client_about;
    this.clientInsuranceStatus = json.client_insurance_status;
    if (json.internal_referral_detail) {
      this.internalReferralDetail = new InternalReferralDetails(json.internal_referral_detail);
    }
  }

  isTopRecMatch(): boolean {
    return this.matchAssignment === MatchAssignmentType.TOP_RECOMMENDED;
  }

  viewableByMember(): boolean {
    return this.aasmState === 'notified' || this.aasmState === 'accepted' || this.aasmState === 'scheduled';
  }

  accepted(): boolean {
    return this.aasmState === 'accepted';
  }

  scheduled(): boolean {
    return this.aasmState === 'scheduled';
  }

  notified(): boolean {
    return this.aasmState === 'notified';
  }

  // eslint-disable-next-line @typescript-eslint/naming-convention
  get member_status(): string {
    switch (this.aasmState) {
      case 'notified':
        return 'new match';

      case 'declined':
        return 'rematched';

      default:
        return this.aasmState;
    }
  }
}

export interface IClientReferralMemberApiResponse {
  /* eslint-disable @typescript-eslint/naming-convention */
  id: number;
  non_clinical_chat_room_uuid: string;
  user_id: number;
  aasm_state: string;
  notified_at: string;
  declined_at: string;
  accepted_at: string;
  patient_id: number;
  client_referral_id: number;
  client_referral: IClientReferralApiResponse;
  calendar_events: SMCalendarEvent[];
  block_number_maxed: boolean;
  session_requested: boolean;
  requested_session_type: RequestedSessionType;
  match_assignment: MatchAssignmentType;
  time_proposals: ITimeProposalApiResponse[];
  time_proposal: boolean;
  client_goals: string;
  client_about: string;
  client_insurance_status: ClientInsuranceStatus;
  internal_referral_detail: InternalReferralDetailsResponse;
  /* eslint-enable @typescript-eslint/naming-convention */
}

export enum RequestedSessionType {
  FREE_CONSULTATION = 'free_consultation',
  SESSION = 'session'
}
